import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { dashboardApi } from '@services/api/dashboard';
import { useAuth } from './useAuth';

// Matches DashboardService.CACHE_TTL_SHORT on the backend (core/services/dashboard_service.py) —
// within that window the backend serves the same cached payload anyway, so treating the query as
// stale sooner only adds a redundant round trip. handleRefresh's invalidateQueries still forces an
// immediate refetch of active queries regardless of staleTime.
const STALE_TIME_SHORT = 30000;

export const useDashboardSummary = (enabled: boolean = true) => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => dashboardApi.getSummary(),
    enabled: enabled && isAuthenticated,
    staleTime: STALE_TIME_SHORT,
    refetchInterval: 30000,
  });
};

export const useDashboardByCounty = (enabled: boolean = true) => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['dashboard', 'byCounty'],
    queryFn: () => dashboardApi.getByCounty(),
    enabled: enabled && isAuthenticated,
    staleTime: STALE_TIME_SHORT,
    refetchInterval: 30000,
  });
};

export const useDashboardByDistrict = (county: string | null, enabled: boolean = true) => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['dashboard', 'byDistrict', county],
    queryFn: () => dashboardApi.getByDistrict(county || undefined),
    // Only per county: the national list is ~16k messy county/district
    // pairs (3.6 MB) and rendering it froze the dashboard.
    enabled: enabled && isAuthenticated && !!county,
    staleTime: STALE_TIME_SHORT,
    refetchInterval: 30000,
    // Without this, the table's data resets to [] the instant the county
    // changes — districtRows then falls through to its "no data" fallback
    // (or the hardcoded sample rows) for a flash on every selection.
    placeholderData: keepPreviousData,
  });
};

export const useDashboardScopeSummary = (
  scope: { county?: string; district?: string; division?: string; location?: string },
  enabled: boolean = true,
) => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['dashboard', 'scopeSummary', scope.county || '', scope.district || '', scope.division || '', scope.location || ''],
    queryFn: () => dashboardApi.getScopeSummary(scope),
    enabled: enabled && isAuthenticated,
    staleTime: STALE_TIME_SHORT,
    refetchInterval: 30000,
    // Keep showing the previous scope's numbers while the new selection loads,
    // instead of flashing the tiles to 0/blank on every dropdown change.
    placeholderData: keepPreviousData,
  });
};

/** Registered voters per polling station, once a location is selected. */
export const useDashboardPollingStations = (
  scope: { county?: string; district?: string; division?: string; location?: string },
) => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['dashboard', 'pollingStations', scope.county || '', scope.district || '', scope.division || '', scope.location || ''],
    queryFn: () => dashboardApi.getPollingStations(scope),
    enabled: isAuthenticated && !!scope.location,
    staleTime: STALE_TIME_SHORT,
  });
};

export const useDashboardByOfficer = (status?: string, enabled: boolean = true) => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['dashboard', 'byOfficer', status],
    queryFn: () => dashboardApi.getByOfficer(status),
    enabled: enabled && isAuthenticated,
    staleTime: STALE_TIME_SHORT,
    refetchInterval: 30000,
  });
};

export const useDashboardTrends = (days: number = 7, enabled: boolean = true) => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['dashboard', 'trends', days],
    queryFn: () => dashboardApi.getTrends(days),
    enabled: enabled && isAuthenticated,
    staleTime: STALE_TIME_SHORT,
    refetchInterval: 300000,
  });
};
